//frontend/src/components/admin/EditDeliveryStaff.js
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import axios from 'axios';
import {
    User,
    Mail,
    Lock,
    Phone,
    MapPin,
    Calendar,
    Save,
    Upload,
    Check,
    Eye,
    EyeOff,
    ArrowLeft,
    RefreshCw
} from 'lucide-react';

const EditDeliveryStaff = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [fetchingData, setFetchingData] = useState(true);
    const [showPassword, setShowPassword] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        loginId: '',
        phone: '',
        address: '',
        gender: 'Male',
        dateOfBirth: '',
        age: '',
        assignedPincodes: []
    });
    const [profilePicture, setProfilePicture] = useState(null);
    const [profilePreview, setProfilePreview] = useState('');
    
    const servingAreas = [
        { code: '562130', area: 'Gollarahatti' },
        { code: '562130', area: 'Kadabagere' },
        { code: '560112', area: 'Chikka Gollarahatti' },
        { code: '560091', area: 'Machohalli' },
        { code: '560091', area: 'Anjana Nagar' },
        { code: '560091', area: 'Byadarahalli' },
        { code: '562130', area: 'Tavarekere' },
        { code: '560091', area: 'Herohalli' },
        { code: '560091', area: 'Viswaneedam' },
        { code: '560091', area: 'Sunkadakatte' },
        { code: '560091', area: 'Muddayanapalya' },
        { code: '560091', area: 'Annapurneshwari Nagar' },
        { code: '560091', area: 'Kottigepalya' },
        { code: '560091', area: 'Sumanahalli' }
    ];

    // Fetch delivery staff data
    const fetchDeliveryStaff = async () => {
        try {
            setFetchingData(true);
            // Add a cache-busting timestamp to prevent cached responses
            const { data } = await axios.get(`/api/v1/admin/delivery-staff/${id}?t=${new Date().getTime()}`, {
                withCredentials: true
            });
            
            if (data.success) {
                const staff = data.deliveryStaff;
                setFormData({
                    name: staff.name,
                    email: staff.email || '',
                    loginId: staff.loginId,
                    phone: staff.phone || '',
                    address: staff.address,
                    gender: staff.gender,
                    dateOfBirth: staff.dateOfBirth ? staff.dateOfBirth.split('T')[0] : '',
                    age: staff.age ? staff.age.toString() : '',
                    assignedPincodes: staff.assignedPincodes || []
                });
                
                if (staff.profilePicture) {
                    // Add cache-busting parameter to the image URL
                    const profileUrl = staff.profilePicture.includes('?')
                        ? `${staff.profilePicture}&t=${new Date().getTime()}`
                        : `${staff.profilePicture}?t=${new Date().getTime()}`;
                    
                    setProfilePreview(profileUrl);
                }
            } else {
                toast.error('Failed to fetch delivery staff details');
                navigate('/delivery-staff-list');
            }
        } catch (error) {
            console.error('Error fetching delivery staff details:', error);
            toast.error(error.response?.data?.message || 'Error fetching delivery staff details');
            navigate('/delivery-staff-list');
        } finally {
            setFetchingData(false);
        }
    };

    useEffect(() => {
        fetchDeliveryStaff();
    }, [id]);

    useEffect(() => {
        if (formData.dateOfBirth) {
            const birthDate = new Date(formData.dateOfBirth);
            const today = new Date();
            let age = today.getFullYear() - birthDate.getFullYear();
            const monthDiff = today.getMonth() - birthDate.getMonth();
            
            if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
                age--;
            }
            
            setFormData(prev => ({
                ...prev,
                age: age.toString()
            }));
        }
    }, [formData.dateOfBirth]);

    const handlePhoneChange = (e) => {
        const value = e.target.value;
        
        // Only allow digits
        const digits = value.replace(/\D/g, '');
        
        // Strictly limit to 10 digits
        const truncatedDigits = digits.slice(0, 10);
        
        // Update form with validated value
        setFormData(prev => ({
          ...prev,
          phone: truncatedDigits
        }));
      };
    
    const handleInputChange = useCallback((e) => {
        const { name, value } = e.target;
        
        if (name === 'phone') {
            return handlePhoneChange(e);
        }
        
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    }, []);

    const handleFileChange = useCallback((e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.size > 5 * 1024 * 1024) {
                toast.error('Image size should be less than 5MB');
                return;
            }
            setProfilePicture(file);
            setProfilePreview(URL.createObjectURL(file));
        }
    }, []);

    const handlePincodeChange = (e) => {
        const { value, checked } = e.target;
        
        setFormData(prev => ({
            ...prev,
            assignedPincodes: checked
                ? [...prev.assignedPincodes, value]
                : prev.assignedPincodes.filter(pin => pin !== value)
        }));
    };

    const togglePasswordVisibility = useCallback(() => {
        setShowPassword(prev => !prev);
    }, []);

    const handleRefresh = () => {
        fetchDeliveryStaff();
        toast.info('Refreshing delivery staff data');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        
        try {
            // Validate fields
            if (!formData.name || !formData.address) {
                toast.error('Please fill in all required fields');
                setSubmitting(false);
                return;
            }
            
            // Phone validation
            if (formData.phone && formData.phone.length !== 10) {
                toast.error('Phone number must be exactly 10 digits');
                setSubmitting(false);
                return;
            }
            
            // Validate assigned pincodes
            if (formData.assignedPincodes.length === 0) {
                toast.error('Please assign at least one service area');
                setSubmitting(false);
                return;
            }
            
            // Create FormData for file upload
            const formPayload = new FormData();
            formPayload.append('name', formData.name);
            formPayload.append('email', formData.email);
            formPayload.append('phone', formData.phone);
            formPayload.append('address', formData.address);
            formPayload.append('gender', formData.gender);
            formPayload.append('dateOfBirth', formData.dateOfBirth);
            
            // Append assignedPincodes as JSON string
            formPayload.append('assignedPincodes', JSON.stringify(formData.assignedPincodes));
            
            // Append profile picture if provided
            if (profilePicture) {
                formPayload.append('profilePicture', profilePicture);
            }
            
            // Log formData for debugging
            console.log('Submitting data:');
            for (let pair of formPayload.entries()) {
                console.log(pair[0] + ': ' + pair[1]);
            }
            
            const { data } = await axios.put(`/api/v1/admin/delivery-staff/${id}`, formPayload, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                },
                withCredentials: true
            });
            
            if (data.success) {
                toast.success('Delivery staff updated successfully');
                setTimeout(() => {
                    navigate('/delivery-staff-list');
                }, 1500);
            }
        } catch (error) {
            console.error('Error updating delivery staff:', error);
            const errorMessage = error.response?.data?.message || 'Error updating delivery staff';
            toast.error(errorMessage);
        } finally {
            setSubmitting(false);
        }
    };

    if (fetchingData) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <RefreshCw className="h-10 w-10 text-[#dc2626] animate-spin" />
                <span className="ml-2 text-base">Loading delivery staff details...</span>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 py-6">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex justify-between items-center mb-4">
                    <button
                        onClick={() => navigate('/delivery-staff-list')}
                        className="flex items-center gap-1.5 text-xs text-gray-600 hover:text-[#dc2626] transition-colors"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        <span>Back to Delivery Staff List</span>
                    </button>
                    
                    <button
                        onClick={handleRefresh}
                        className="flex items-center gap-1.5 text-xs text-gray-600 hover:text-[#dc2626] transition-colors"
                    >
                        <RefreshCw className="w-4 h-4" />
                        <span>Refresh Data</span>
                    </button>
                </div>
                
                <div className="bg-white rounded-xl shadow-lg p-4 md:p-6">
                    <h2 className="text-xl font-bold text-[#dc2626] mb-4 font-['Crimson_Text'] text-center">
                        Edit Delivery Staff
                    </h2>
                    <form onSubmit={handleSubmit} className="space-y-4">
                        {/* Profile Picture */}
                        <div className="flex flex-col items-center justify-center">
                            <div className="mb-3">
                                {profilePreview ? (
                                    <img
                                        src={profilePreview}
                                        alt="Profile preview"
                                        className="h-28 w-28 rounded-full object-cover border-4 border-gray-200"
                                        onError={(e) => {
                                            console.error("Image load error");
                                            e.target.src = "/images/default_avatar.png";
                                        }}
                                    />
                                ) : (
                                    <div className="h-28 w-28 rounded-full bg-gray-200 flex items-center justify-center">
                                        <User className="h-14 w-14 text-gray-400" />
                                    </div>
                                )}
                            </div>
                            
                            <input
                                type="file"
                                id="profilePicture"
                                accept="image/*"
                                onChange={handleFileChange}
                                className="hidden"
                            />
                            
                            <label
                                htmlFor="profilePicture"
                                className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-md shadow-sm text-xs font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#dc2626] cursor-pointer"
                            >
                                <Upload className="h-4 w-4 mr-1.5" />
                                {profilePreview ? 'Change Photo' : 'Upload Photo'}
                            </label>
                        </div>
                        
                        {/* Basic Information - First Row */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {/* Name */}
                            <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                    Full Name
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                                        <User className="h-4 w-4 text-gray-400" />
                                    </div>
                                    <input
                                        type="text"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        className="w-full pl-9 pr-3 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-[#dc2626] focus:border-[#dc2626] transition-colors"
                                        placeholder="Enter full name"
                                        required
                                    />
                                </div>
                            </div>
                            
                            {/* Login ID */}
                            <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                    Login ID
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                                        <User className="h-4 w-4 text-gray-400" />
                                    </div>
                                    <input
                                        type="text"
                                        name="loginId"
                                        value={formData.loginId}
                                        onChange={handleInputChange}
                                        className="w-full pl-9 pr-3 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-[#dc2626] focus:border-[#dc2626] transition-colors bg-gray-50"
                                        placeholder="Enter login ID"
                                        required
                                        readOnly
                                    />
                                </div>
                            </div>
                        </div>
                        
                        {/* Second Row */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {/* Email */}
                            <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                    Email (Optional)
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                                        <Mail className="h-4 w-4 text-gray-400" />
                                    </div>
                                    <input
                                        type="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleInputChange}
                                        className="w-full pl-9 pr-3 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-[#dc2626] focus:border-[#dc2626] transition-colors"
                                        placeholder="Enter email address"
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                    Phone Number
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                                        <Phone className="h-4 w-4 text-gray-400" />
                                    </div>
                                    <input
                                        type="tel"
                                        inputMode="numeric"
                                        pattern="[0-9]{10}"
                                        name="phone"
                                        value={formData.phone}
                                        onChange={handlePhoneChange}
                                        maxLength="10"
                                        className="w-full pl-9 pr-3 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-[#dc2626] focus:border-[#dc2626] transition-colors"
                                        placeholder="Enter 10-digit phone number"
                                        required
                                        onInput={(e) => {
                                            // Ensure only numbers are entered
                                            e.target.value = e.target.value.replace(/\D/g, '');
                                            // Ensure maximum length is enforced
                                            if (e.target.value.length > 10) {
                                                e.target.value = e.target.value.slice(0, 10);
                                            }
                                        }}
                                        onKeyPress={(e) => {
                                            // Allow only numeric input and prevent typing more than 10 digits
                                            const char = String.fromCharCode(e.which);
                                            const currentLength = e.target.value.length;
                                            
                                            if (!/[0-9]/.test(char) || currentLength >= 10) {
                                                e.preventDefault();
                                            }
                                        }}
                                    />
                                </div>
                                {formData.phone && formData.phone.length !== 10 && (
                                    <p className="mt-1 text-xs text-red-500">
                                        Phone number must be exactly 10 digits
                                    </p>
                                )}
                            </div>
                        </div>
                        
                        {/* Third Row */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {/* Date of Birth */}
                            <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                    Date of Birth
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                                        <Calendar className="h-4 w-4 text-gray-400" />
                                    </div>
                                    <input
                                        type="date"
                                        name="dateOfBirth"
                                        value={formData.dateOfBirth}
                                        onChange={handleInputChange}
                                        max={new Date().toISOString().split('T')[0]}
                                        className="w-full pl-9 pr-3 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-[#dc2626] focus:border-[#dc2626] transition-colors"
                                        required
                                    />
                                </div>
                            </div>
                            
                            {/* Age (Auto-calculated) */}
                            <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                    Age
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                                        <User className="h-4 w-4 text-gray-400" />
                                    </div>
                                    <input
                                        type="text"
                                        value={`${formData.age} years`}
                                        className="w-full pl-9 pr-3 py-1.5 text-xs border border-gray-300 rounded-lg bg-gray-50 text-gray-500"
                                        readOnly
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Gender Selection */}
                        <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                                Gender
                            </label>
                            <div className="flex space-x-4 mt-1">
                                {['Male', 'Female', 'Other'].map((option) => (
                                    <div key={option} className="flex items-center">
                                        <input
                                            type="radio"
                                            id={option.toLowerCase()}
                                            name="gender"
                                            value={option}
                                            checked={formData.gender === option}
                                            onChange={handleInputChange}
                                            className="h-3 w-3 text-[#dc2626] focus:ring-[#dc2626] border-gray-300"
                                        />
                                        <label htmlFor={option.toLowerCase()} className="ml-1.5 text-xs text-gray-700">
                                            {option}
                                        </label>
                                    </div>
                                ))}
                            </div>
                        </div>
                        
                        {/* Address */}
                        <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                                Address
                            </label>
                            <div className="relative">
                                <div className="absolute top-2 left-3">
                                    <MapPin className="h-4 w-4 text-gray-400" />
                                </div>
                                <textarea
                                    name="address"
                                    value={formData.address}
                                    onChange={handleInputChange}
                                    rows="3"
                                    className="w-full pl-9 pr-3 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-[#dc2626] focus:border-[#dc2626] transition-colors"
                                    placeholder="Enter full address"
                                    required
                                />
                            </div>
                        </div>
                        
                        {/* Service Areas */}
                        <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                                Assigned Service Areas
                            </label>
                            <div className="max-h-40 overflow-y-auto border border-gray-300 rounded-lg p-2">
                                {servingAreas.map((area) => (
                                    <div key={area.code} className="flex items-center space-x-2 p-1.5 hover:bg-gray-50 rounded-lg">
                                        <div
                                            className={`w-4 h-4 border rounded flex items-center justify-center cursor-pointer transition-colors
                                                ${formData.assignedPincodes.includes(area.code) ? 'bg-[#dc2626] border-[#dc2626]' : 'border-gray-300'}`}
                                            onClick={() => {
                                                const isSelected = formData.assignedPincodes.includes(area.code);
                                                handlePincodeChange({
                                                    target: {
                                                        value: area.code,
                                                        checked: !isSelected
                                                    }
                                                });
                                            }}
                                        >
                                            {formData.assignedPincodes.includes(area.code) && <Check className="h-3 w-3 text-white" />}
                                        </div>
                                        <label className="flex-1 cursor-pointer text-xs">
                                            {area.area} ({area.code})
                                        </label>
                                    </div>
                                ))}
                            </div>
                            {formData.assignedPincodes.length === 0 && (
                                <p className="mt-1 text-xs text-red-500">
                                    Please select at least one service area
                                </p>
                            )}
                        </div>
                        
                        {/* Submit Button */}
                        <div className="flex justify-center">
                            <button
                                type="submit"
                                disabled={submitting}
                                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg shadow-sm text-white bg-[#dc2626] hover:bg-[#dc2626]/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#dc2626] transition-colors disabled:opacity-50"
                            >
                                {submitting ? (
                                    <>
                                        <RefreshCw className="w-4 h-4 mr-1.5 animate-spin" />
                                        Updating...
                                    </>
                                ) : (
                                    <>
                                        <Save className="w-4 h-4 mr-1.5" />
                                        Update Delivery Staff
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
            
            {/* Mobile Optimization Styles */}
            <style jsx>{`
                @media (max-width: 768px) {
                    .flex-col {
                        flex-direction: column;
                    }
                    
                    .gap-4 {
                        gap: 1rem;
                    }
                    
                    .px-3 {
                        padding-left: 0.75rem;
                        padding-right: 0.75rem;
                    }
                    
                    .py-1.5 {
                        padding-top: 0.375rem;
                        padding-bottom: 0.375rem;
                    }
                    
                    .text-xs {
                        font-size: 0.75rem;
                        line-height: 1rem;
                    }
                }
            `}</style>
        </div>
    );
};

export default EditDeliveryStaff;